import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "next-i18next";
import clsx from "clsx";
import Image from "next/image";

import { SearchArtistFragment, SearchArtistIndexDocument } from "@/graphql/types";
import SearchResult from "@/components/SearchResult";
import SearchFilter from "@/components/filters/SearchFilter";
import FilterItem from "@/components/filters/FilterItem";
import CloseButton from "@/components/ui/svg/CloseButton";
import { initializeApollo } from "@/apollo";
import { NO_IMAGE_URL } from "@/utils/constants";

/** Props for {@link ArtistSearchFilter} */
interface Props {
  selectedArtists: SearchArtistFragment[];
  setSelectedArtists: Dispatch<SetStateAction<SearchArtistFragment[]>>;
  /** Called when user selects an artist */
  onSelect?: () => void;
}

/**
 * Renders a search input to filter by artists
 *
 * @param props - Component props
 */
export default function ArtistSearchFilter({ selectedArtists, setSelectedArtists, onSelect }: Props) {
  const { t } = useTranslation(["filters", "common"]);

  return (
    <FilterItem
      label={
        selectedArtists.length > 0
          ? t("common:common.counts.artist", { count: selectedArtists.length })
          : t("filters:filters.artists.label_other")
      }
      key="artists-dropdown"
    >
      <SearchFilter
        label={t("filters:filters.artists.by")}
        searchFunction={async (text) => {
          const client = initializeApollo();

          const query = await client.query({
            query: SearchArtistIndexDocument,
            variables: { query: text, limit: 10 },
          });

          return { artists: query.data.searchArtistIndex };
        }}
        selected={
          <div className="grid grid-cols-2 gap-3 mb-3 max-h-[116px] overflow-y-auto">
            {selectedArtists.map((artist) => (
              <div
                key={artist.id}
                className={clsx(
                  "px-2 h-10 w-full rounded box-border flex items-center gap-2",
                  "min-w-0 bg-dark-6/40 relative",
                  "before:border before:bg-dark-stroke before:border-gradient before:rounded"
                )}
              >
                <div className="shrink-0 h-6 w-6">
                  <Image
                    src={artist.avatarUrl ?? NO_IMAGE_URL}
                    alt={artist.name}
                    width="24"
                    height="24"
                    className="rounded-full"
                  />
                </div>
                <div className="flex-grow truncate text-sm leading-1.25 font-medium">{artist.name}</div>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedArtists((artists) => [...artists.filter((a) => a.id !== artist.id)])
                  }
                  className={clsx(
                    "w-5 h-5 rounded-full flex-shrink-0 flex items-center",
                    "justify-center bg-dark-8 text-dark-6"
                  )}
                >
                  <CloseButton width="12" height="12" />
                </button>
              </div>
            ))}
          </div>
        }
      >
        {(results: { artists: SearchArtistFragment[] } | undefined | void) =>
          !results || results.artists.length > 0 ? (
            <div className={"grid grid-cols-2 gap-3 max-h-[132px] overflow-y-auto"}>
              {results?.artists
                .filter((artist) => !selectedArtists.some((a) => a.id === artist.id))
                .map((artist) => (
                  <button
                    key={artist.id}
                    onClick={() => {
                      setSelectedArtists((prev) => [...prev, artist]);
                      onSelect?.();
                    }}
                    className="text-left"
                  >
                    <SearchResult artist={artist} small />
                  </button>
                ))}
            </div>
          ) : (
            <span className="text-center w-full">{t("filters:filters.none")}</span>
          )
        }
      </SearchFilter>
    </FilterItem>
  );
}
